import { isChordNotes, isChordVariant } from './chord-types.js'
import type { Chord } from './chord-types.js'

/**
 * Quality symbols and their words, longest first so `maj` wins over `m` and `m7b5` over `m` (Bowie, GH #2488).
 * `m7b5` is read as a whole: a half-diminished chord, its 7th implied.
 */
const qualityWords: [symbol: string, words: string][] = [
  ['m7b5', 'half-diminished'],
  ['ø7', 'half-diminished'],
  ['ø', 'half-diminished'],
  ['maj', 'major'],
  ['Maj', 'major'],
  ['min', 'minor'],
  ['dim', 'diminished'],
  ['aug', 'augmented'],
  ['sus', 'suspended'],
  ['add', 'add'],
  ['°', 'diminished'],
  ['+', 'augmented'],
  ['M', 'major'],
  ['m', 'minor'],
]

const accidentalWords: Record<string, string> = { '#': 'sharp', b: 'flat' }

/** "F#" → "F sharp", "Bb" → "B flat" */
const spokenNote = (note: string) => {
  const accidental = accidentalWords[note.slice(1)]
  return accidental ? `${note[0]} ${accidental}` : note
}

/** "m7b5" → ["half-diminished"], "7#11" → ["7", "sharp 11"], "sus4" → ["suspended", "4"] */
const spokenTokens = (rest: string): string[] => {
  if (rest.length === 0) {
    return []
  }
  const quality = qualityWords.find(([symbol]) => rest.startsWith(symbol))
  if (quality) {
    return [quality[1], ...spokenTokens(rest.slice(quality[0].length))]
  }
  const degree = /^\d+/.exec(rest)?.[0]
  if (degree) {
    return [degree, ...spokenTokens(rest.slice(degree.length))]
  }
  const alteration = /^([#b])(\d+)/.exec(rest)
  if (alteration) {
    return [`${accidentalWords[alteration[1]]} ${alteration[2]}`, ...spokenTokens(rest.slice(alteration[0].length))]
  }
  /** Punctuation such as parentheses carries nothing to say */
  return spokenTokens(rest.slice(1))
}

const spokenVariant = (variant: string) => spokenTokens(variant).join(' ')

/** Root, quality and bass from a chord symbol such as "C7/E" */
const fromSymbol = (symbol: string) => {
  const [chordPart, bass] = symbol.split('/')
  const match = /^([A-G][#b]?)(.*)$/.exec(chordPart ?? '')
  return match ? { root: match[1], variant: match[2], bass } : undefined
}

/**
 * A chord as words for a screen reader: "A minor 7", "F sharp minor 7", "B flat major 7", "C 7 over E" (Bowie,
 * GH #2488). Screen readers spell symbols out ("bee bee maj seven"), so this is the accessible name; the
 * visible label stays the symbol (`getName`).
 */
export const spokenChordName = (chord: Chord): string => {
  const parts = isChordVariant(chord)
    ? { root: chord.root, variant: chord.variant ?? '', bass: chord.bassNote }
    : isChordNotes(chord)
      ? fromSymbol(chord.name)
      : undefined
  if (!parts) {
    return ''
  }
  const quality = spokenVariant(parts.variant)
  return [spokenNote(parts.root), quality, parts.bass ? `over ${spokenNote(parts.bass)}` : ''].filter(Boolean).join(' ')
}
